import { useState } from "react";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import DrawRequestIcon from "Assets/Img/DrawRequestIcon";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyPreconReportIcon from "Assets/Img/EmptyPreconReportIcon";
import { formatDate } from "utils/Common/Date";
import Table, { Column } from "Components/Table/Table";
import Button from "Components/Common/Button/Button";
import ShareReportModal from "Components/Common/ShareReportModal/ShareReportModal";
import { useToast } from "Components/Common/Toast/ToastContext";
import { createShareLink } from "services/projectService";

interface TocItem {
  label: string;
  id: string;
}

interface KvItem {
  label: string;
  value: string;
  bold?: boolean;
}

interface CurrencyAmount {
  amount: number;
  currency: { code: string; symbol: string; name: string };
}

interface Entity {
  id?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
}

interface BudgetLineItem {
  tradeName: string;
  scheduledValue: CurrencyAmount | null;
  projectSf: string;
  costPerSf: string | null;
  industryLow: string | null;
  industryHigh: string | null;
  notes: string | null;
  displayOrder: number;
}

interface ActionItem {
  id?: string;
  party?: string;
  actionDescription?: string;
  status?: string;
  displayOrder?: number;
}

interface PreconSectionProps {
  preconData?: {
    id: string;
    projectId: string;
    publishedAt: string;
    invoiceHeader: {
      reportDate: string;
      invoiceNumber: string;
      invoiceDate: string;
      invoiceAmount: CurrencyAmount;
      inspectionDate: string;
      servicesRequested: string;
      preparedBy: {
        nameAndTitle: string;
        companyName: string;
        phone: string;
        email: string;
        reportDate: string;
      };
      lenderName: string;
      lendingOfficerName: string;
      isPublished: boolean;
    } | null;
    certification: {
      project: {
        name: string;
        address: string;
        description: string;
        projectType: string;
        contractValue: CurrencyAmount;
        updatedContractValue: CurrencyAmount;
      };
      lender: { id: string; companyName: string; branchName: string } | null;
      lendingOfficer: Entity | null;
      gc: Entity | null;
      gcPrimaryContact: Entity | null;
      owner: Entity | null;
      architect: Entity | null;
      servicesRequested: string;
      reportDate: string;
      opening: string;
      standards: string;
      findings: string;
      signer: string;
      signerCompanyName: string;
      signerEmail: string;
      signerPhone: string;
    } | null;
    projectDescription: {
      description: string;
      scopeNotes: string;
      totalSf: string;
      totalUnitsPhase: number;
      totalUnitsFull: number;
      totalBudget: CurrencyAmount;
      hardCostBudget: CurrencyAmount;
      costPerSf: string | null;
      mobilizationDate: string;
      estCompletionDate: string;
    } | null;
    contractDelivery: {
      contractType: string;
      contractorFee: CurrencyAmount | null;
      contingencyAmount: CurrencyAmount | null;
      retainage: string;
      keyFindings: string;
    } | null;
    allowancesContingencies: {
      allowancesNarrative: string;
      gcContingencyAmount: CurrencyAmount | null;
      gcContingencyPercentage: string;
      contingencyTypicalRange: string;
      contingencyNarrative: string;
      clarificationsNarrative: string;
    } | null;
    budgetAnalysis: {
      narrative: string;
      conclusion: string;
      lineItems: BudgetLineItem[];
      comparisonProjects: {
        name: string;
        units: number;
        budget: CurrencyAmount | null;
        costLessSitework: CurrencyAmount | null;
        costPerUnit: CurrencyAmount | null;
        totalSf: string;
        costPerSf: CurrencyAmount | null;
        displayOrder: number;
      }[];
    } | null;
    scheduleAnalysis: {
      masterScheduleProvided: boolean;
      typicalDuration: string;
      statedDuration: string;
      narrative: string;
      mobilizationDate: string;
      estCompletionDate: string;
    } | null;
    closingComments: string;
    actionItems: ActionItem[];
    documentsReviewedList: string;
  } | null;
  loading?: boolean;
  projectId?: string;
  preconReportId?: string;
}

const TOC_ITEMS: TocItem[] = [
  { label: "Project Overview", id: "ps-cert" },
  { label: "Project Description", id: "ps-desc" },
  { label: "Contract Delivery & Review", id: "ps-contract" },
  { label: "Allowances & Contingencies", id: "ps-allowances" },
  { label: "Budget Analysis", id: "ps-budget" },
  { label: "Schedule Analysis", id: "ps-schedule" },
  { label: "Closing Comments", id: "ps-closing" },
  { label: "Action Items", id: "ps-actions" },
];

function fmtAmount(amount: number | null | undefined, symbol = "$"): string {
  if (amount == null || isNaN(amount)) return "—";
  return `${symbol}${amount?.toLocaleString()}`;
}

export default function PreconSection({
  preconData,
  loading = false,
  projectId,
  preconReportId,
}: PreconSectionProps) {
  const showToast = useToast();
  const [sharing, setSharing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const handleConfirmSharePrecon = async (emails: string[]) => {
    if (!projectId) return;
    setSharing(true);
    try {
      const res: any = await createShareLink(projectId, {
        shareLinkType: "precon_report",
        role: "lender",
        resourceId: preconReportId,
        emails,
      });
      if (res?.error) {
        showToast(res.error.message || "Failed to generate share link.");
        return;
      }
      showToast("Precon report shared successfully to the lender.");
      setShowShareModal(false);
    } catch {
      showToast("Failed to generate share link.");
    } finally {
      setSharing(false);
    }
  };

  const invoiceHeader = preconData?.invoiceHeader;
  const certification = preconData?.certification;
  const project = certification?.project;
  const projectDesc = preconData?.projectDescription;
  const contractDelivery = preconData?.contractDelivery;
  const allowances = preconData?.allowancesContingencies;
  const budgetAnalysis = preconData?.budgetAnalysis;
  const scheduleAnalysis = preconData?.scheduleAnalysis;
  const actionItems = preconData?.actionItems ?? [];

  const totalAmount =
    budgetAnalysis?.lineItems?.reduce(
      (sum, item) => sum + (item.scheduledValue?.amount ?? 0),
      0,
    ) ?? 0;
  const totalSymbol =
    budgetAnalysis?.lineItems?.[0]?.scheduledValue?.currency?.symbol ?? "$";

  const fmtEntity = (entity: Entity | null) =>
    [
      entity?.companyName,
      [entity?.firstName, entity?.lastName].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(" · ") || "—";

  const overviewItems: KvItem[] = [
    {
      label: "Bank / Lender",
      bold: true,
      value:
        [
          certification?.lender?.branchName ??
            certification?.lender?.companyName,
          certification?.lendingOfficer
            ? `${certification.lendingOfficer.firstName ?? ""} ${certification.lendingOfficer.lastName ?? ""}`.trim()
            : null,
        ]
          .filter(Boolean)
          .join(" · ") || "—",
    },
    { label: "Project", value: project?.name ?? "—", bold: true },
    { label: "Description", value: project?.description ?? "—" },
    { label: "Owner", value: fmtEntity(certification?.owner ?? null) },
    { label: "Contractor", value: fmtEntity(certification?.gc ?? null) },
    { label: "Services", value: certification?.servicesRequested || "—" },
  ];

  if (!loading && !preconData) {
    return (
      <div>
        <h1 className="sectionTitle">Pre-Construction Report</h1>
        <div className="mt-3">
          <EmptyState
            icon={<EmptyPreconReportIcon />}
            title="Report Not Generated Yet"
            subtitle="The Pre-Construction Report will be available after the pre-construction authoring report has been submitted."
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="ps-header-row">
        <h1 className="sectionTitle">Pre-Construction Report</h1>
        <Button
          className="report-tab active"
          onClick={() => setShowShareModal(true)}
          disabled={loading}
          title="Share precon report to lender"
        >
          Share Precon
        </Button>
      </div>
      {loading ? (
        <Skeleton width={100} height={20} style={{ marginBottom: 10 }} />
      ) : (
        <div className="section-sub">
          {project?.name ?? "—"} · Prepared by{" "}
          {invoiceHeader?.preparedBy?.companyName ?? ""}
          {invoiceHeader?.reportDate
            ? ` · ${formatDate(invoiceHeader.reportDate, false)}`
            : ""}
        </div>
      )}

      {/* ── Quick Navigation ── */}
      <div className="card ps-nav-card">
        <div className="card-title">Quick Navigation</div>
        <div className="precon-toc">
          {TOC_ITEMS.map((item) => (
            <Button
              className="toc-item"
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              type="button"
            >
              <div className="items-icon">
                <DrawRequestIcon />
              </div>
              {item.label}
            </Button>
          ))}
        </div>
      </div>

      {/* ── Main report card ── */}
      <div className="card preconOverview">
        {/* Invoice header */}

        <div className="projectInvoice">
          {loading ? (
            Array.from({ length: 1 }).map((_, i) => (
              <div key={i} className="ps-skeleton-row">
                <Skeleton
                  width={800}
                  height={40}
                  style={{ marginBottom: 10 }}
                />
              </div>
            ))
          ) : (
            <>
              <div>
                <div className="projectInvoiceLabel">
                  {invoiceHeader
                    ? `CSC Invoice ${invoiceHeader.invoiceNumber} · ${formatDate(invoiceHeader.invoiceDate, false)}`
                    : "—"}
                </div>
                <div className="projectInvoiceDec">
                  {[
                    certification?.lender?.branchName ??
                      certification?.lender?.companyName ??
                      invoiceHeader?.lenderName,
                    invoiceHeader?.lendingOfficerName,
                    "Preconstruction Report",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <div className="projectInvoiceRight">
                <div className="projectInvoiceVal">
                  {invoiceHeader
                    ? fmtAmount(
                        invoiceHeader.invoiceAmount?.amount,
                        invoiceHeader.invoiceAmount?.currency?.symbol,
                      )
                    : "—"}
                </div>
              </div>
            </>
          )}
        </div>

        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="ps-skeleton-row">
              <Skeleton width={220} height={13} style={{ marginBottom: 10 }} />
              <Skeleton width="100%" height={80} borderRadius={6} />
            </div>
          ))
        ) : (
          <>
            {/* ps-cert: Project Overview */}
            <div className="precon-section" id="ps-cert">
              <div className="ps-title">Project Overview</div>
              <div className="ps-overview-grid">
                {overviewItems.map((item) => (
                  <div key={item.label}>
                    <span className="ps-kv-label">{item.label}:</span>{" "}
                    <span className="ps-kv-label">
                      {" "}
                      {item.bold ? <strong>{item.value}</strong> : item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* ps-desc: Project Description */}
            <div className="precon-section" id="ps-desc">
              <div className="ps-title">
                Project Description / Executive Summary
              </div>
              <div className="ps-body">
                {projectDesc ? (
                  <ul>
                    {projectDesc?.description && (
                      <li>{projectDesc?.description}</li>
                    )}
                    {projectDesc?.scopeNotes && (
                      <li>{projectDesc?.scopeNotes}</li>
                    )}
                    {projectDesc?.totalSf && (
                      <li>
                        Approximate total square footage:{" "}
                        <b>
                          {" "}
                          {parseFloat(
                            projectDesc?.totalSf,
                          )?.toLocaleString()}{" "}
                          SF
                        </b>
                        {projectDesc?.totalUnitsPhase
                          ? ` · ${projectDesc?.totalUnitsPhase} units (Phase) / ${projectDesc?.totalUnitsFull} total`
                          : ""}
                      </li>
                    )}
                    {projectDesc?.hardCostBudget && (
                      <li>
                        Hard cost construction budget:{" "}
                        <b>
                          {" "}
                          {fmtAmount(
                            projectDesc?.hardCostBudget.amount,
                            projectDesc?.hardCostBudget.currency.symbol,
                          )}
                        </b>
                        {projectDesc?.costPerSf
                          ? ` (approx. $${projectDesc?.costPerSf}/SF)`
                          : ""}
                      </li>
                    )}
                    {projectDesc?.totalBudget && (
                      <li>
                        Total budget including soft costs:{" "}
                        <b>
                          {" "}
                          {fmtAmount(
                            projectDesc?.totalBudget.amount,
                            projectDesc?.totalBudget.currency.symbol,
                          )}
                        </b>
                      </li>
                    )}
                    {(projectDesc?.mobilizationDate ||
                      projectDesc?.estCompletionDate) && (
                      <li>
                        {[
                          projectDesc?.mobilizationDate
                            ? `Mobilization: ${formatDate(projectDesc?.mobilizationDate, false)}`
                            : null,
                          projectDesc?.estCompletionDate
                            ? `Estimated substantial completion: ${formatDate(projectDesc?.estCompletionDate, false)}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </li>
                    )}
                  </ul>
                ) : (
                  <span className="ps-empty">—</span>
                )}
              </div>
            </div>

            {/* ps-contract: Contract Delivery & Review */}
            <div className="precon-section" id="ps-contract">
              <div className="ps-title">
                Contract Delivery Method &amp; Review
              </div>
              <div className="ps-body">
                {contractDelivery ? (
                  <ul>
                    {contractDelivery.contractType && (
                      <li>
                        {contractDelivery.contractType}
                        {certification?.gc?.companyName
                          ? ` — ${certification.gc.companyName} acting as Owner/General Contractor`
                          : ""}
                        {projectDesc?.totalBudget
                          ? ` with budget of ${fmtAmount(projectDesc?.totalBudget.amount, projectDesc?.totalBudget.currency.symbol)}`
                          : ""}
                      </li>
                    )}
                    <li>
                      Contractor Fee:{" "}
                      {(contractDelivery.contractorFee?.amount ?? 0) > 0
                        ? fmtAmount(
                            contractDelivery.contractorFee?.amount,
                            contractDelivery.contractorFee?.currency.symbol,
                          )
                        : "No Contractor Fee carried"}
                    </li>
                    <li>
                      Contingency:{" "}
                      {fmtAmount(
                        contractDelivery.contingencyAmount?.amount,
                        contractDelivery.contingencyAmount?.currency.symbol,
                      )}
                      {projectDesc?.hardCostBudget?.amount &&
                      contractDelivery.contingencyAmount?.amount
                        ? ` (${((contractDelivery.contingencyAmount.amount / projectDesc?.hardCostBudget.amount) * 100).toFixed(1)}% of hard costs)`
                        : ""}
                    </li>
                    <li>
                      {contractDelivery.retainage != null
                        ? `${contractDelivery.retainage}% retainage stated`
                        : "0% retainage stated"}{" "}
                      — 5–10% is typical for a project of this nature. Lender
                      should verify retainage requirements with Lending
                      Agreement
                    </li>
                    {contractDelivery.keyFindings && (
                      <li>{contractDelivery.keyFindings}</li>
                    )}
                  </ul>
                ) : (
                  <span className="ps-empty">—</span>
                )}
              </div>
            </div>

            {/* ps-allowances: Allowances & Contingencies */}
            <div className="precon-section" id="ps-allowances">
              <div className="ps-title">Allowances &amp; Contingencies</div>
              <div className="ps-body">
                {allowances ? (
                  <div className="ps-narrative-stack">
                    {allowances.allowancesNarrative && (
                      <p>
                        <strong>Allowances:</strong>{" "}
                        {allowances.allowancesNarrative}
                      </p>
                    )}
                    <p>
                      <strong>Contingency:</strong>{" "}
                      {fmtAmount(
                        allowances.gcContingencyAmount?.amount,
                        allowances.gcContingencyAmount?.currency.symbol,
                      )}{" "}
                      ({allowances.gcContingencyPercentage}% of hard costs).
                      {allowances.contingencyTypicalRange && (
                        <>
                          {" "}
                          Typical for projects of this nature:{" "}
                          {allowances.contingencyTypicalRange}
                        </>
                      )}
                    </p>
                    {allowances.contingencyNarrative && (
                      <p>{allowances.contingencyNarrative}</p>
                    )}
                    {allowances.clarificationsNarrative && (
                      <p>{allowances.clarificationsNarrative}</p>
                    )}
                  </div>
                ) : (
                  <span className="ps-empty">—</span>
                )}
              </div>
            </div>

            {/* ps-budget: Budget Analysis */}
            <div className="precon-section" id="ps-budget">
              <div className="ps-title">
                Construction Budget Analysis — Major Trade Comparison
              </div>
              {budgetAnalysis?.narrative && (
                <div className="ps-body ps-budget-narrative">
                  {budgetAnalysis.narrative}
                </div>
              )}
              {budgetAnalysis?.lineItems?.length ? (
                <Table
                  tableClassName="budget-table"
                  data={budgetAnalysis.lineItems}
                  columns={
                    [
                      { header: "Scope", render: (r) => r.tradeName },
                      {
                        header: "Scheduled Value",
                        render: (r) =>
                          fmtAmount(
                            r.scheduledValue?.amount,
                            r.scheduledValue?.currency.symbol,
                          ),
                      },
                      {
                        header: "Project SF",
                        render: (r) => r.projectSf ?? "—",
                      },
                      {
                        header: "Project $/SF",
                        render: (r) => (r.costPerSf ? `$${r.costPerSf}` : "—"),
                      },
                      {
                        header: "Industry Low",
                        render: (r) => r.industryLow ?? "—",
                      },
                      {
                        header: "Industry High",
                        render: (r) => r.industryHigh ?? "—",
                      },
                    ] as Column<BudgetLineItem>[]
                  }
                  footerRow={[
                    "Totals",
                    fmtAmount(totalAmount, totalSymbol),
                    "—",
                    "—",
                    "—",
                    "—",
                  ]}
                  footerRowClassName="total-row"
                />
              ) : (
                <span style={{ color: "var(--tx3)" }}>—</span>
              )}
              {budgetAnalysis?.comparisonProjects?.length ? (
                <>
                  <div className="precon-subtitle">
                    Comparison to Similar Projects
                  </div>
                  <Table
                    tableClassName="comp-table"
                    data={budgetAnalysis.comparisonProjects
                      .slice()
                      .sort((a, b) => a.displayOrder - b.displayOrder)}
                    columns={
                      [
                        { header: "Project", render: (p) => p.name },
                        { header: "Units", render: (p) => p.units },
                        {
                          header: "Budget",
                          render: (p) =>
                            fmtAmount(
                              p.budget?.amount,
                              p.budget?.currency?.symbol,
                            ),
                        },
                        {
                          header: "Cost Less Sitework",
                          render: (p) =>
                            fmtAmount(
                              p.costLessSitework?.amount,
                              p.costLessSitework?.currency?.symbol,
                            ),
                        },
                        {
                          header: "Cost / Unit",
                          render: (p) =>
                            fmtAmount(
                              p.costPerUnit?.amount,
                              p.costPerUnit?.currency?.symbol,
                            ),
                        },
                        {
                          header: "Total SF",
                          render: (p) =>
                            parseFloat(p.totalSf)?.toLocaleString(),
                        },
                        {
                          header: "Cost / SF",
                          render: (p) =>
                            fmtAmount(
                              p.costPerSf?.amount,
                              p.costPerSf?.currency?.symbol,
                            ),
                        },
                      ] as Column<
                        (typeof budgetAnalysis.comparisonProjects)[number]
                      >[]
                    }
                  />
                </>
              ) : null}
              {budgetAnalysis?.conclusion && (
                <div className="precon-alert">
                  <strong>Budget Analysis Conclusion:</strong>{" "}
                  {budgetAnalysis.conclusion}
                </div>
              )}
            </div>

            {/* ps-schedule: Schedule Analysis */}
            <div className="precon-section" id="ps-schedule">
              <div className="ps-title">Schedule Analysis</div>
              <div className="ps-body">
                {scheduleAnalysis ? (
                  <ul>
                    <li>
                      {scheduleAnalysis.masterScheduleProvided
                        ? "Master Schedule provided"
                        : "No Master Schedule provided"}
                    </li>
                    {(scheduleAnalysis.statedDuration ||
                      scheduleAnalysis.mobilizationDate ||
                      scheduleAnalysis.estCompletionDate) && (
                      <li>
                        Owner states
                        {scheduleAnalysis.statedDuration
                          ? ` ${scheduleAnalysis.statedDuration} timeframe`
                          : ""}
                        {scheduleAnalysis.mobilizationDate
                          ? `: mobilization ${formatDate(scheduleAnalysis.mobilizationDate, false)}`
                          : ""}
                        {scheduleAnalysis.estCompletionDate
                          ? `, substantial completion ${formatDate(scheduleAnalysis.estCompletionDate, false)}`
                          : ""}
                      </li>
                    )}
                    {scheduleAnalysis.typicalDuration && (
                      <li>
                        Projects of this nature typically take{" "}
                        {scheduleAnalysis.typicalDuration} to complete
                      </li>
                    )}
                    <li>
                      Lender reminded that unforeseen conditions (weather,
                      material shortages, labor strikes, tariffs) may affect
                      schedule
                    </li>
                    {scheduleAnalysis.narrative && (
                      <li>{scheduleAnalysis.narrative}</li>
                    )}
                  </ul>
                ) : (
                  <span className="ps-empty">—</span>
                )}
              </div>
            </div>

            {/* ps-closing: Closing Comments */}
            <div className="precon-section" id="ps-closing">
              <div className="ps-title">Closing Comments / Summary</div>
              <div className="ps-body">
                {preconData?.closingComments ? (
                  <p>{preconData.closingComments}</p>
                ) : (
                  <span className="ps-empty">—</span>
                )}
              </div>
            </div>

            {/* ps-actions: Action Items */}
            <div className="precon-section" id="ps-actions">
              <div className="ps-title">Action Items</div>
              {actionItems.length ? (
                <Table
                  tableClassName="action-table"
                  data={actionItems
                    .slice()
                    .sort(
                      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
                    )}
                  columns={
                    [
                      {
                        header: "Party",
                        className: "at-party",
                        render: (item) => item.party ?? "—",
                      },
                      {
                        header: "Action Item",
                        render: (item) => item.actionDescription ?? "—",
                      },
                      {
                        header: "Status",
                        className: "at-status",
                        render: (item) => {
                          const statusMap: Record<
                            string,
                            { cls: string; label: string }
                          > = {
                            complete: { cls: "b-active", label: "Complete" },
                            accepted: { cls: "b-active", label: "Accepted" },
                            pending: { cls: "b-review", label: "Pending" },
                            na: { cls: "b-active", label: "N/A" },
                          };
                          const s = statusMap[item.status ?? ""] ?? {
                            cls: "b-review",
                            label: "Pending",
                          };
                          return (
                            <span className={`badge ${s.cls}`}>
                              <span className="bdot" />
                              {s.label}
                            </span>
                          );
                        },
                      },
                    ] as Column<ActionItem>[]
                  }
                />
              ) : (
                <span style={{ color: "var(--tx3)" }}>—</span>
              )}
            </div>

            {/* Documents Reviewed */}
            <div className="precon-section">
              <div className="ps-title">Documents Reviewed</div>
              <div className="ps-body">
                {preconData?.documentsReviewedList ? (
                  <p>{preconData.documentsReviewedList}</p>
                ) : (
                  <span className="ps-empty">—</span>
                )}
              </div>
            </div>

            {/* Prepared by footer */}
            <div className="ps-prepared-by">
              Prepared by:{" "}
              <strong>
                {invoiceHeader?.preparedBy?.nameAndTitle
                  ? invoiceHeader?.preparedBy?.nameAndTitle + " · "
                  : ""}{" "}
                {invoiceHeader?.preparedBy?.companyName
                  ? invoiceHeader?.preparedBy?.companyName + " · "
                  : ""}
              </strong>
              {invoiceHeader?.reportDate
                ? `${formatDate(invoiceHeader.reportDate, false)}`
                : ""}
            </div>
          </>
        )}
      </div>

      <ShareReportModal
        show={showShareModal}
        title="Share Pre-Construction Report"
        loading={sharing}
        onClose={() => setShowShareModal(false)}
        onConfirm={handleConfirmSharePrecon}
      />
    </div>
  );
}
